/** Markdown → sanitised HTML for the note view. */

import DOMPurify from "dompurify";
import { marked } from "marked";

marked.setOptions({
  gfm: true,
  // Standard markdown folds a single newline into a space; only a blank line
  // or a trailing double-space starts a new line. That is the wrong default
  // for a note-taking tool: notes are typed one thought per line, and having
  // those lines run together in the rendered view hides structure the author
  // put there on purpose. `breaks` makes every newline in the source a <br>,
  // so the rendered note keeps the line shape of the file. It does not affect
  // paragraphs (a blank line still starts a new <p>), lists or fenced code.
  breaks: true,
});

/**
 * Renders markdown into an element. Note bodies come from the user's own
 * notes, but they round-trip through a server and a sync daemon, so the output
 * is sanitised rather than trusted.
 */
export function renderMarkdownInto(target: HTMLElement, markdown: string): void {
  const html = marked.parse(markdown, { async: false });
  target.innerHTML = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

  for (const anchor of Array.from(target.querySelectorAll("a[href]"))) {
    const href = anchor.getAttribute("href") ?? "";
    if (/^https?:/i.test(href)) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    }
  }
}

/** First non-heading line of a body, for list previews. */
export function excerpt(markdown: string, maxLength = 180): string {
  const plain = markdown
    .replace(/^---[\s\S]*?---/, "")
    .replace(/^#{1,6}\s.*$/gm, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/[*_`>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length <= maxLength ? plain : `${plain.slice(0, maxLength).trimEnd()}…`;
}
